import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, api } from "@/lib/api";

const EMPTY = { label: "", value: "", supporting_text: "", sort_order: 0 };

export default function AdminStatistics() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/statistics").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load statistics"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, ...body } = editing;
    body.sort_order = Number(body.sort_order) || 0;
    try {
      if (id) {
        await adminApi.put(`/admin/statistics/${id}`, body);
        toast.success("Statistic updated");
      } else {
        await adminApi.post("/admin/statistics", body);
        toast.success("Statistic created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete statistic "${row.label}"?`)) return;
    try {
      await adminApi.delete(`/admin/statistics/${row.id}`);
      toast.success("Statistic deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-statistics-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Statistics</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Key figures displayed across the public website.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="statistics-add-btn">
          <Plus size={15} className="mr-2" /> Add statistic
        </Button>
      </div>

      <div className="mt-5 bg-white border border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Value</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Supporting text</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} data-testid={`statistic-row-${s.id}`}>
                <TableCell className="font-semibold text-neutral-900">{s.value}</TableCell>
                <TableCell className="text-neutral-600">{s.label}</TableCell>
                <TableCell className="text-neutral-500 max-w-md truncate">{s.supporting_text}</TableCell>
                <TableCell className="text-neutral-500">{s.sort_order}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...EMPTY, ...s })} data-testid={`statistic-edit-${s.id}`}>
                    <Pencil size={15} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`statistic-delete-${s.id}`}>
                    <Trash2 size={15} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="statistic-dialog">
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{editing.id ? "Edit statistic" : "Add statistic"}</DialogTitle>
                <DialogDescription>Key figure shown on the public website.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Value *</Label>
                  <Input required placeholder="e.g. 24+" value={editing.value} onChange={(e) => set("value")(e.target.value)} data-testid="statistic-value-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Label *</Label>
                  <Input required placeholder="e.g. Years in market" value={editing.label} onChange={(e) => set("label")(e.target.value)} data-testid="statistic-label-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Supporting text</Label>
                <Input value={editing.supporting_text} onChange={(e) => set("supporting_text")(e.target.value)} data-testid="statistic-supporting-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Sort order</Label>
                <Input type="number" value={editing.sort_order} onChange={(e) => set("sort_order")(e.target.value)} data-testid="statistic-sort-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="statistic-save-btn">
                  {busy ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
